import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Play, TrendingUp, Target, DollarSign, Zap, ArrowRight, BarChart3, Globe, Bot } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function CaucheraLanding() {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    products_detected: 0,
    agents_active: 4,
    markets: ["US", "UK", "DE", "CA", "AU"]
  });

  useEffect(() => {
    // Fetch basic stats
    fetch(`${API_URL}/api/cauchera/dashboard/agents/status`)
      .then(res => res.json())
      .then(data => {
        if (data.success) {
          setStats(prev => ({
            ...prev,
            agents_active: data.data.summary.active
          }));
        }
      })
      .catch(console.error);
  }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-black via-zinc-900 to-black" />
        
        {/* Animated grid */}
        <div className="absolute inset-0 opacity-20">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(rgba(255,255,255,0.05) 1px, transparent 1px), 
                             linear-gradient(90deg, rgba(255,255,255,0.05) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }} />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-6 text-center">
          {/* Logo */}
          <div className="mb-8">
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-zinc-800 bg-zinc-900/50">
              <Zap className="w-5 h-5 text-emerald-400" />
              <span className="text-sm font-medium text-zinc-400">100% Automatizado con IA</span>
            </div>
          </div>

          <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold tracking-tight mb-6">
            <span className="bg-gradient-to-r from-white via-zinc-300 to-zinc-500 bg-clip-text text-transparent">
              CAUCHERA
            </span>
          </h1>
          
          <p className="text-xl md:text-2xl text-zinc-400 max-w-3xl mx-auto mb-4">
            Portal de Ventas Dropshipping
          </p>
          
          <p className="text-lg text-zinc-500 max-w-2xl mx-auto mb-12">
            Detectamos productos con demanda al alza en micro-comunidades globales.
            <br />
            Capitalizamos la oportunidad. Maximizamos ROI. Salimos en el momento justo.
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-16">
            <button 
              onClick={() => navigate('/cauchera/dashboard')}
              className="group px-8 py-4 bg-white text-black font-semibold rounded-lg hover:bg-zinc-200 transition-all flex items-center justify-center gap-2"
            >
              Acceder al Dashboard
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button 
              onClick={() => navigate('/cauchera/demo')}
              className="px-8 py-4 border border-zinc-700 text-white font-semibold rounded-lg hover:bg-zinc-900 transition-all flex items-center justify-center gap-2"
            >
              <Play className="w-5 h-5" />
              Ver Demo
            </button>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-emerald-400 mb-1">
                {stats.agents_active}/12
              </div>
              <div className="text-sm text-zinc-500">Agentes IA Activos</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-emerald-400 mb-1">
                24/7
              </div>
              <div className="text-sm text-zinc-500">Escaneo Continuo</div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-emerald-400 mb-1">
                5+
              </div>
              <div className="text-sm text-zinc-500">Mercados Globales</div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-zinc-700 rounded-full flex items-start justify-center p-2">
            <div className="w-1.5 h-3 bg-zinc-500 rounded-full animate-pulse" />
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="py-24 px-6 bg-zinc-950">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-4">
            Cómo Funciona
          </h2>
          <p className="text-zinc-400 text-center mb-16 max-w-2xl mx-auto">
            Un sistema de agentes de IA que opera 24/7 sin intervención humana
          </p>

          <div className="grid md:grid-cols-5 gap-8">
            {[
              { icon: TrendingUp, title: "Detectar", desc: "Escaneamos tendencias globales" },
              { icon: Target, title: "Validar", desc: "Analizamos demanda y competencia" },
              { icon: DollarSign, title: "Calcular", desc: "Proyectamos ROI y márgenes" },
              { icon: Globe, title: "Ejecutar", desc: "Lanzamos tienda y campañas" },
              { icon: BarChart3, title: "Optimizar", desc: "Escalamos o salimos" },
            ].map((step, i) => (
              <div key={i} className="relative group">
                <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 hover:border-emerald-500/50 transition-all h-full">
                  <div className="w-12 h-12 bg-emerald-500/10 rounded-lg flex items-center justify-center mb-4">
                    <step.icon className="w-6 h-6 text-emerald-400" />
                  </div>
                  <h3 className="font-semibold text-lg mb-2">{step.title}</h3>
                  <p className="text-sm text-zinc-500">{step.desc}</p>
                </div>
                {i < 4 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-px bg-zinc-800" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dream Team Section */}
      <section className="py-24 px-6 bg-black">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Bot className="w-8 h-8 text-emerald-400" />
            <h2 className="text-3xl md:text-4xl font-bold">Dream Team de Agentes</h2>
          </div>
          <p className="text-zinc-400 text-center mb-16 max-w-2xl mx-auto">
            12 agentes especializados trabajando en coordinación
          </p>

          <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { name: "TREND_SCANNER", status: "active", desc: "Detecta tendencias 24/7" },
              { name: "DEMAND_DETECTOR", status: "pending", desc: "Valida demanda real" },
              { name: "SUPPLIER_MATCHER", status: "active", desc: "Encuentra proveedores" },
              { name: "PRICING_ENGINE", status: "active", desc: "Calcula precios" },
              { name: "ROI_VALIDATOR", status: "active", desc: "Valida rentabilidad" },
              { name: "STORE_BUILDER", status: "pending", desc: "Crea micro-tiendas" },
              { name: "VIDEO_ENHANCER", status: "pending", desc: "Mejora videos con IA" },
              { name: "AD_OPTIMIZER", status: "pending", desc: "Optimiza campañas" },
            ].map((agent, i) => (
              <div 
                key={i}
                className={`p-4 rounded-lg border ${
                  agent.status === 'active' 
                    ? 'bg-emerald-500/5 border-emerald-500/30' 
                    : 'bg-zinc-900/50 border-zinc-800'
                }`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-2 h-2 rounded-full ${
                    agent.status === 'active' ? 'bg-emerald-400' : 'bg-zinc-600'
                  }`} />
                  <span className="font-mono text-sm">{agent.name}</span>
                </div>
                <p className="text-xs text-zinc-500">{agent.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 bg-gradient-to-b from-zinc-950 to-black">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            ¿Listo para catapultar oportunidades?
          </h2>
          <p className="text-zinc-400 mb-8">
            Accede al dashboard y comienza a detectar productos con demanda al alza
          </p>
          <button 
            onClick={() => navigate('/cauchera/dashboard')}
            className="px-8 py-4 bg-emerald-500 text-black font-semibold rounded-lg hover:bg-emerald-400 transition-all"
          >
            Comenzar Ahora
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-900">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-emerald-400" />
            <span className="font-bold">CAUCHERA</span>
          </div>
          <div className="text-sm text-zinc-500">
            Portal de Ventas Dropshipping 100% Automatizado con IA
          </div>
        </div>
      </footer>
    </div>
  );
}
